import Phaser from "phaser";
import { getHatSpriteKey } from "../models/common";

export type KillerInfo = {
  victimId: string;
  killerName?: string;
  killerProfileIcon?: number;
  killerBannerText?: string;
  killerBorder?: number;
  killerSkin?: string;
  killerHat?: number;
};

// Shown for 4s so the victim has time to read it before the death screen appears
// (main-scene.ts delays the BaseScene transition to match).
const LIFETIME_MS = 4000;

const ICON_RADIUS = 56;
const ICON_MARGIN = 90;

/**
 * Fixed-position message shown to the VICTIM when killed by another player or a
 * bot: the killer's equipped border framing the whole screen, their profile icon
 * (round-cropped) in the upper-right corner, "KILLED BY" + their name centered at
 * the top, and their equipped banner text centered on screen. Self-manages its
 * own tween/timer and exposes destroy() for scene shutdown, mirroring TeleportArrow.
 */
export class KillMessage {
  private container: Phaser.GameObjects.Container;
  private dismissTimer: Phaser.Time.TimerEvent | null = null;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container, info: KillerInfo) {
    const { width, height } = scene.scale;
    const iconX = width - ICON_MARGIN;
    const iconY = ICON_MARGIN;

    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(150000);
    this.container.setAlpha(0);
    uiLayer.add(this.container);

    const borderKey = info.killerBorder && info.killerBorder > 0 ? `border-${info.killerBorder}` : null;
    if (borderKey && scene.textures.exists(borderKey)) {
      const border = scene.add.image(width / 2, height / 2, borderKey);
      border.setDisplaySize(width, height);
      this.container.add(border);
    }

    const iconKey =
      info.killerProfileIcon && info.killerProfileIcon > 0
        ? `profile-icon-${info.killerProfileIcon}`
        : "profile-icon";
    if (scene.textures.exists(iconKey)) {
      const icon = scene.add.image(iconX, iconY, iconKey);
      icon.setDisplaySize(ICON_RADIUS * 2, ICON_RADIUS * 2);

      // Crop the icon into a circle - the mask graphics itself must stay hidden
      // and share the container so it lines up with the icon under the uiCamera.
      const mask = scene.add.graphics();
      mask.fillStyle(0xffffff);
      mask.fillCircle(iconX, iconY, ICON_RADIUS);
      mask.setVisible(false);
      icon.setMask(mask.createGeometryMask());
      this.container.add([icon, mask]);
    }

    const ring = scene.add.graphics();
    ring.lineStyle(4, 0xfbbf24, 1);
    ring.strokeCircle(iconX, iconY, ICON_RADIUS);
    this.container.add(ring);

    const nameY = 66;
    let nameBottom = 34 + 20; // fallback if there's no name, so the sprite still has a home

    if (info.killerName) {
      this.container.add(
        scene.add
          .text(width / 2, 34, "KILLED BY", {
            fontSize: "16px",
            fontFamily: "Anton",
            color: "#e2e8f0",
          })
          .setOrigin(0.5)
          .setShadow(1, 1, "#000000", 0.7),
      );
      const nameText = scene.add
        .text(width / 2, nameY, info.killerName, {
          fontSize: "34px",
          fontFamily: "Anton",
          color: "#ffffff",
        })
        .setOrigin(0.5)
        .setShadow(2, 2, "#000000", 0.7);
      this.container.add(nameText);
      nameBottom = nameY + nameText.height / 2;
    }

    // Skin sprite sits below the killer name; the banner text below that.
    const skinSize = 110;
    const skinY = nameBottom + 20 + skinSize / 2;
    let skinBottom = skinY - skinSize / 2;

    const skinKey = info.killerSkin && scene.textures.exists(info.killerSkin) ? info.killerSkin : "player";
    if (scene.textures.exists(skinKey)) {
      const skinSprite = scene.add.sprite(width / 2, skinY, skinKey, 0);
      skinSprite.setDisplaySize(skinSize, skinSize);

      const skinAnimKey = `${skinKey}-idle`;
      if (!scene.anims.exists(skinAnimKey)) {
        const frames = scene.anims.generateFrameNumbers(skinKey, { start: 0, end: 1 });
        if (frames && frames.length > 0) {
          scene.anims.create({ key: skinAnimKey, frames, frameRate: 4, repeat: -1 });
        }
      }
      if (scene.anims.exists(skinAnimKey)) {
        skinSprite.play(skinAnimKey);
      }
      this.container.add(skinSprite);
      skinBottom = skinY + skinSize / 2;

      // Hat sits on the skin sprite's head, same relative placement as Player.HAT_Y_OFFSET
      // (bottom-anchored at the frame's vertical center), scaled up to the skin sprite's size.
      const hatKey = getHatSpriteKey(info.killerHat);
      if (hatKey && scene.textures.exists(hatKey)) {
        const hatSprite = scene.add.sprite(width / 2, skinY, hatKey, 0);
        hatSprite.setOrigin(0.5, 1);
        hatSprite.setDisplaySize(skinSize, skinSize);
        this.container.add(hatSprite);
      }
    }

    if (info.killerBannerText) {
      this.container.add(
        scene.add
          .text(width / 2, skinBottom + 30, info.killerBannerText, {
            fontSize: "60px",
            fontFamily: "Anton",
            color: "#fbbf24",
            align: "center",
            wordWrap: { width: width * 0.85 },
          })
          .setOrigin(0.5, 0)
          .setShadow(3, 3, "#000000", 0.8),
      );
    }

    scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 300,
      ease: "Back.easeOut",
    });

    this.dismissTimer = scene.time.delayedCall(LIFETIME_MS, () => this.dismiss(scene));
  }

  private dismiss(scene: Phaser.Scene): void {
    if (!this.container?.scene) return;
    scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 250,
      onComplete: () => this.destroy(),
    });
  }

  destroy(): void {
    this.dismissTimer?.remove();
    this.dismissTimer = null;
    if (this.container?.scene) {
      this.container.destroy();
    }
  }
}
